import { datadogRum } from '@datadog/browser-rum';
import { Mixpanel, MxE } from './mixpanelWrapper';

// Client-side analog of bpm-lambdas' lib/alertThrottle.ts rollup: report the first occurrence of
// a given (operation, errorType) pair in full, then collapse repeats within the same window into
// periodic rollups instead of one report per occurrence — so a stuck poll loop or a hammering
// background refetch can't quietly generate hundreds of near-identical reports. sessionStorage
// stands in for the backend's shared Redis counter (necessarily per-browser-session here, not
// global).
const THROTTLE_WINDOW_MS = 60 * 60 * 1000; // 1 hour, matches alertThrottle.ts's window
const ROLLUP_EVERY = 25; // report occurrence 1, then every 25th (25, 50, 75...) as a rollup
const STORAGE_PREFIX = 'errorReportThrottle:';

interface ThrottleEntry {
  windowStart: number;
  count: number;
}

const readThrottleEntry = (key: string): ThrottleEntry | null => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null; // sessionStorage unavailable (private mode, quota) — degrades to "report everything", which is safe
  }
};

const writeThrottleEntry = (key: string, entry: ThrottleEntry) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Ignore — see readThrottleEntry.
  }
};

// Bumps and persists the (operation, errorType) occurrence count, rolling the window over once
// THROTTLE_WINDOW_MS has elapsed since it started. Shared by every reporting path below so they
// all collapse repeats the same way, under the same counter.
const bumpThrottleCount = (operation: string, errorType: string, fingerprint?: string): number => {
  const key = `${STORAGE_PREFIX}${operation}:${errorType}${fingerprint ? `:${fingerprint}` : ''}`;
  const now = Date.now();
  const existing = readThrottleEntry(key);
  const withinWindow = existing && now - existing.windowStart < THROTTLE_WINDOW_MS;
  const windowStart = withinWindow ? existing.windowStart : now;
  const count = withinWindow ? existing.count + 1 : 1;
  writeThrottleEntry(key, { windowStart, count });
  return count;
};

const isReportableOccurrence = (count: number): boolean =>
  count === 1 || count % ROLLUP_EVERY === 0;

const reportToSdk = (
  error: unknown,
  operation: string,
  errorType: string,
  occurrenceCount: number,
  extra?: Record<string, unknown>
) => {
  datadogRum.addError(error, { operation, errorType, occurrenceCount, ...extra });
};

export const resolveErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

// Mirrored to Mixpanel (see ClientErrorProps in mixpanelWrapper.ts) alongside every SDK report, so
// patterns across errors — which operation, how often, on which page — are queryable the same way
// lambda_fetch_failed already is, instead of visible only one incident at a time in Datadog.
const trackErrorInMixpanel = (
  operation: string,
  errorType: string,
  error: unknown,
  occurrenceCount: number
): void => {
  Mixpanel.track(MxE.ClientErrorReported, {
    operation,
    error_type: errorType,
    page: window.location.pathname,
    error_message: resolveErrorMessage(error),
    occurrence_count: occurrenceCount
  });
};

export interface ReportErrorOptions {
  // Low-cardinality identifier for what failed (a query key's base name, a hook, a page) — not
  // the raw error message, so the throttle key stays stable across repeats of the same failure.
  operation: string;
  errorType?: string; // defaults to the error's constructor name
  // Extra debugging context to attach to the report (e.g. an ID or a step name). Kept as a flat
  // key/value bag rather than SDK-specific tags/extra shapes.
  extra?: Record<string, unknown>;
  // Stable per-bug identifier (the FE- error id) folded into the throttle key.
  fingerprint?: string;
}

export const reportError = (
  error: unknown,
  { operation, errorType, extra, fingerprint }: ReportErrorOptions
): void => {
  const resolvedErrorType =
    errorType ?? (error instanceof Error ? error.constructor.name : 'Unknown');
  const count = bumpThrottleCount(operation, resolvedErrorType, fingerprint);

  if (isReportableOccurrence(count)) {
    reportToSdk(error, operation, resolvedErrorType, count, extra);
    trackErrorInMixpanel(operation, resolvedErrorType, error, count);
  }
};
